import './EvaluationFormView.scss';
import { ProjectEvaluation, CurrencyCode } from '@belimo-retrofit-portal/logic';
import { FormController } from '@form-ts/core';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'src/modules/common/components/Button';
import { CurrencySymbol } from 'src/modules/common/components/CurrencySymbol';
import { TileCard } from 'src/modules/common/components/TileCard';
import { DECIMAL_PLACES, MAX_TRILLION } from 'src/modules/common/constants/decimal';
import { sub, sup } from 'src/modules/common/constants/formattingHelpers';
import { MagicIcon } from 'src/modules/common/icons/MagicIcon';
import { AiResponse } from 'src/modules/common/types/AiResponse';
import { Future } from 'src/modules/common/types/Future';
import { DecimalField } from 'src/modules/form/components/DecimalField';
import { TextAreaField } from 'src/modules/form/components/TextAreaField';
import { FormError } from 'src/modules/form/types/FormError';

type Props = {
  readonly form: FormController<ProjectEvaluation, FormError>;
  readonly currency: CurrencyCode;
  readonly generatedProposal: Future<AiResponse> | null;
  readonly readOnly: boolean;
  readonly onGenerateProposal: () => void;
};

export const EvaluationFormView = ({
  form,
  currency,
  generatedProposal,
  readOnly,
  onGenerateProposal,
}: Props): React.ReactElement => (
  <div className="bp-evaluation-form-view">
    <TileCard title={(
      <FormattedMessage
        id="evaluation/form/initial/title"
        values={{ sub, sup }}
      />
    )}
    >
      <div className="bp-evaluation-form-view__field">
        <DecimalField
          size="md"
          field={form.field.at('investmentAmount')}
          autoComplete="off"
          decimalScale={DECIMAL_PLACES}
          maxValue={MAX_TRILLION}
          label={<FormattedMessage id="evaluation/form/initial/investmentAmount/label"/>}
          actions={<CurrencySymbol currency={currency}/>}
          description={(
            <FormattedMessage
              id="evaluation/form/initial/investmentAmount/note"
              values={{ sub, sup }}
            />
          )}
          readOnly={readOnly}
        />
      </div>

      <div className="bp-evaluation-form-view__field">
        <DecimalField
          size="md"
          field={form.field.at('carbonEmissionPrice')}
          autoComplete="off"
          decimalScale={DECIMAL_PLACES}
          maxValue={MAX_TRILLION}
          label={(
            <FormattedMessage
              id="evaluation/form/initial/carbonEmissionPrice/label"
              values={{ sub, sup }}
            />
          )}
          actions={(
            <span>
              <FormattedMessage
                id="evaluation/form/initial/carbonEmissionPrice/unit"
                values={{ sub: sub, sup: sup, currency: <CurrencySymbol currency={currency}/> }}
              />
            </span>
          )}
          readOnly={readOnly}
        />
      </div>
    </TileCard>

    <TileCard
      title={(
        <div className="bp-evaluation-form-view__tile-title">
          <FormattedMessage id="evaluation/form/proposal/title"/>

          {!readOnly && (
            <Button
              kind="tertiary"
              onClick={onGenerateProposal}
              size="sm"
              loading={generatedProposal?.state === 'pending'}
              icon={<MagicIcon/>}
              className="bp-evaluation-form-view__ai-button"
            >
              <FormattedMessage id="evaluation/form/proposal/writeWithAI"/>

              <div className="bp-evaluation-form-view__ai-beta">
                <FormattedMessage id="evaluation/form/proposal/beta"/>
              </div>
            </Button>
          )}
        </div>
      )}
    >
      <div className="bp-evaluation-form-view__field">
        <TextAreaField
          field={form.field.at('proposalDescription')}
          label={<FormattedMessage id="evaluation/form/proposal/description/label"/>}
          rows={6}
          maxLength={280}
          readOnly={readOnly}
        />
      </div>
    </TileCard>
  </div>
);
